import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra } from '@app/store';
import {
  GetVacationScheduleParams,
  VacationSchedule,
  VacationsService,
} from '@app/gen/vacations';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { SettingsState, SettingsWithRootState } from '../../settings.state';

import { getVacationsCompanies } from './vacations-list.actions';

export const getSchedule = createAsyncThunk<
  VacationSchedule,
  GetVacationScheduleParams & { company_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'vacations/getSchedule',
  async (
    getScheduleDto,
    { dispatch, getState, rejectWithValue, extra: { inject } },
  ) => {
    try {
      const vacationsService = inject(VacationsService);

      const companies = getState().settings.vacationsList.companies;
      const companyId = getScheduleDto.company_id;

      if (
        !companies.length ||
        !companies.find((company) => company.company_id === companyId)
      ) {
        await dispatch(getVacationsCompanies());

        const {
          settings: {
            vacationsList: { companies: requestedCompanies },
          },
        } = getState();

        if (
          !requestedCompanies.find(
            (company) => company.company_id === companyId,
          )
        ) {
          const error = new AppError('client', {
            code: 404,
            message: 'Компания не найдена',
          });

          error.title = 'Компания не найдена';

          throw error;
        }
      }

      return await vacationsService.getVacationSchedule({
        params: { schedule_id: getScheduleDto.schedule_id },
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsVacationDetailReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getSchedule.pending, (state) => {
    state.vacationDetail.status = 'loading';
    state.vacationDetail.data = null;
    state.vacationDetail.error = null;
  });
  builder.addCase(getSchedule.fulfilled, (state, { payload }) => {
    state.vacationDetail.status = 'complete';
    state.vacationDetail.data = payload;
    state.vacationDetail.error = null;
  });
  builder.addCase(getSchedule.rejected, (state, { payload, error }) => {
    state.vacationDetail.status = 'failed';
    state.vacationDetail.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
